export default (node) => {
  const loader = node.querySelector('[data-wishlist-loader]');
  const emptyState = node.querySelector('[data-wishlist-empty]');
  const errorState = node.querySelector('[data-wishlist-error]');
  const itemsContainer = node.querySelector('[data-wishlist-items]');
  const retryButton = node.querySelector('[data-wishlist-retry]');

  // Get money format, button text, and labels from data attributes
  const moneyFormat = node.dataset.moneyFormat || '{{amount}} kr';
  const shopCurrency = node.dataset.shopCurrency || 'DKK';
  const labelLoading = node.dataset.loadingText || 'Loading ...';
  const labelAdd = node.dataset.labelAdd || 'Add to wishlist';
  const labelRemove = node.dataset.labelRemove || 'Remove from wishlist';

  // Get translated text strings from data attributes
  const textNotifyMe = node.dataset.textNotifyMe || 'Notify me';
  const textSelectSize = node.dataset.textSelectSize || 'Select size';
  const textChooseAmount = node.dataset.textChooseAmount || 'Choose amount';
  const textAddToCart = node.dataset.textAddToCart || 'Add to cart';
  const textPreorder = node.dataset.textPreorder || 'Preorder';
  const textMto = node.dataset.textMto || 'Made to order';
  const textGoToProduct = node.dataset.textGoToProduct || 'Go to product';
  const textFrom = node.dataset.textFrom || 'From';
  const textPrefix = node.dataset.textPrefix || '+';

  // Store ResizeObserver reference for cleanup
  let resizeObserver = null;

  // Helper: Format money (price in cents) using Shopify's money format
  const formatMoney = (cents) => {
    // Validate input
    if (cents === null || cents === undefined || isNaN(cents)) {
      return moneyFormat.replace('{{amount}}', '0.00');
    }
    const amount = cents / 100;

    // Format the amount based on the money format string
    let formattedAmount = amount.toFixed(2);

    // Handle different decimal and thousands separators
    if (moneyFormat.includes('{{amount_with_comma_separator}}')) {
      // European format: 1.234,56
      formattedAmount = amount
        .toFixed(2)
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return moneyFormat.replace(
        '{{amount_with_comma_separator}}',
        formattedAmount
      );
    } else if (
      moneyFormat.includes('{{amount_no_decimals_with_comma_separator}}')
    ) {
      // European format without decimals: 1.234
      formattedAmount = Math.round(amount)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return moneyFormat.replace(
        '{{amount_no_decimals_with_comma_separator}}',
        formattedAmount
      );
    } else if (moneyFormat.includes('{{amount_with_space_separator}}')) {
      // Format with space separator: 1 234,56
      formattedAmount = amount
        .toFixed(2)
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return moneyFormat.replace(
        '{{amount_with_space_separator}}',
        formattedAmount
      );
    } else if (moneyFormat.includes('{{amount}}')) {
      // Standard format: 1,234.56
      formattedAmount = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return moneyFormat.replace('{{amount}}', formattedAmount);
    }

    // Fallback
    return moneyFormat.replace('{{amount}}', formattedAmount);
  };

  // Get product URL path from full URL
  const getProductPath = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch (error) {
      return url;
    }
  };

  // Render product using Swym data directly
  const renderProduct = (wishlistItem) => {
    // Use Swym data directly to build product card
    return buildProductCard(wishlistItem);
  };

  // Helper: Build product splashes (badges) - matching product-splashes.liquid
  const buildProductSplashes = (item, shopifyData, cprops) => {
    const badges = [];
    const tagClass =
      'bg-cream flex items-center justify-center px-1.5 py-[9px] transition-all duration-300 text-xxs leading-[1] uppercase text-black group-hover:bg-[#f7f5ef26] group-hover:text-cream';
    const saleClass =
      'bg-black flex items-center justify-center px-1.5 py-[9px] transition-all duration-300 text-xxs leading-[1] uppercase text-cream group-hover:bg-accent group-hover:text-cream';

    // Tags - use fresh tags from shopifyData, fallback to cprops
    const tags = shopifyData?.tags || cprops?.tags || [];

    // Check for GWP and exclude tags
    const isGWP = tags.includes('GWP');
    const isExclude = tags.includes('exclude');
    const isConfigurable = tags.includes('CONFIGURABLE');

    // Sale badge - use fresh compare_at_price from shopifyData
    const comparePrice =
      shopifyData?.compare_at_price || cprops?.compare_at_price || 0;
    if (!isGWP && !isExclude && comparePrice && comparePrice > item.pr) {
      const discount = Math.round(
        ((comparePrice - item.pr) / comparePrice) * 100
      );
      badges.push(`<div class="sale ${saleClass}">${discount}%</div>`);
    }

    // New badge
    if (tags.includes('New')) {
      badges.push(`<div class="new ${tagClass}">New</div>`);
    }

    // Certified badge - from tags or metafield
    const isCertified =
      tags.includes('responsible') ||
      tags.includes('certified') ||
      cprops?.certified;
    if (isCertified) {
      badges.push(`<div class="certified ${tagClass}">Certified</div>`);
    }

    // MTO badge - from cprops metafield
    const outOfStockStrategy = cprops?.out_of_stock_strategy?.trim();
    if (outOfStockStrategy === 'made_to_order') {
      badges.push(`<div class="mto ${tagClass}">${textMto}</div>`);
    }

    // CANLIS badge
    if (tags.includes('CANLIS')) {
      badges.push(`<div class="canlis ${tagClass}">Can Lis</div>`);
    }

    // CONFIGURABLE badge
    if (isConfigurable) {
      badges.push(`<div class="configurable ${tagClass}">Configurable</div>`);
    }

    if (badges.length === 0) return '';

    return `<div class="absolute left-3.5 top-3.5 z-10 mr-1.5 flex flex-row flex-wrap items-center gap-1.5">${badges.join('')}</div>`;
  };

  // Helper: Build color selector - matching color-select.liquid for product_thumb
  const buildColorSelector = (cprops, productHandle) => {
    if (!cprops || !cprops.web_color_hex) {
      // Return empty spacer like in product-thumb.liquid when no colors
      return `<div class="mb-2.5 flex h-[52px] gap-0.5 p-4">&nbsp;</div>`;
    }

    const colorCircles = [];
    const maxColors = 4; // Limit for product thumbs

    // Current product color circle (no border-black-05, just border)
    const hasSecondaryColor = cprops.secondary_color_hex;
    const title = hasSecondaryColor
      ? `${cprops.web_color_name || ''}/${cprops.secondary_color_name || ''}`
      : cprops.web_color_name || '';

    colorCircles.push(`
      <a
        href="/products/${productHandle}"
        class="relative rotate-45 cursor-pointer overflow-hidden rounded-full border-black-05 p-0 h-3 w-3 border"
        data-color-handle="${productHandle}"
        title="${title}"
        data-hex="${cprops.web_color_hex}"
        style="order: 0"
      >
        ${
          hasSecondaryColor
            ? `
          <div class="absolute h-full w-1/2" style="background-color: ${cprops.web_color_hex}"></div>
          <div class="absolute right-0 h-full w-1/2" style="background-color: ${cprops.secondary_color_hex}"></div>
        `
            : `
          <div class="absolute h-full w-full" style="background-color: ${cprops.web_color_hex}"></div>
        `
        }
      </a>
    `);

    // Related products color circles (up to maxColors)
    if (cprops.related_products && cprops.related_products.length > 0) {
      cprops.related_products.slice(0, maxColors).forEach((related) => {
        if (!related || !related.web_color_hex || !related.handle) return;

        const relatedHasSecondary = related.secondary_color_hex;
        const relatedTitle = relatedHasSecondary
          ? `${related.web_color_name || ''}/${related.secondary_color_name || ''}`
          : related.web_color_name || '';

        colorCircles.push(`
          <a
            href="/products/${related.handle}"
            class="relative rotate-45 cursor-pointer overflow-hidden rounded-full border border-black-05 p-0 h-3 w-3"
            data-color-handle="${related.handle}"
            title="${relatedTitle}"
            data-hex="${related.web_color_hex}"
          >
            ${
              relatedHasSecondary
                ? `
              <div class="absolute h-full w-1/2" style="background-color: ${related.web_color_hex}"></div>
              <div class="absolute right-0 h-full w-1/2" style="background-color: ${related.secondary_color_hex}"></div>
            `
                : `
              <div class="absolute h-full w-full" style="background-color: ${related.web_color_hex}"></div>
            `
            }
          </a>
        `);
      });
    }

    return `
      <div class="flex gap-0.5 absolute bottom-0 mb-0 p-4">
        ${colorCircles.join('')}
      </div>
    `;
  };

  // Helper: Build add to cart button - matching add-to-cart.liquid for product_thumb
  const buildAddToCartButton = (item, shopifyData, cprops) => {
    const productUrl = item?.du || '#';
    const variantId = item?.epi || '';
    const productTitle = item?.dt || '';
    const productPrice = item?.pr || 0;

    // Base button classes from button.liquid
    const baseButtonClass =
      'font-secondary box-border flex h-12 w-fit max-w-full cursor-pointer items-center justify-center border border-solid border-black px-[14px] py-0 text-sm font-medium no-underline transition-all duration-300 ease-in-out';
    // Outline button style (default for product thumbs)
    const outlineButtonClass =
      'hover:text-cream bg-transparent text-black hover:bg-black disabled:cursor-not-allowed disabled:opacity-40';
    // Extra classes for product thumb buttons
    const extraClass = 'w-full !justify-start';

    const baseClass = `${baseButtonClass} ${outlineButtonClass} ${extraClass}`;

    // Determine product state
    const outOfStockStrategy = cprops?.out_of_stock_strategy?.trim();
    const isAvailable = shopifyData?.available !== false;
    const variants = shopifyData?.variants || [];
    const hasMultipleVariants = variants.length > 1;
    const tags = shopifyData?.tags || [];
    const isGWP = tags.includes('GWP');
    const isBookEasy = tags.includes('bookeasy');
    const isGiftCard = shopifyData?.type === 'Gift Card';

    // Check if any variant is available
    let anyVariantAvailable = isAvailable;
    if (!isAvailable && hasMultipleVariants) {
      anyVariantAvailable = variants.some((v) => v.available);
    }

    const isMTO = outOfStockStrategy === 'made_to_order';
    const isPreorder = outOfStockStrategy === 'preorder';
    const isSoldOut = outOfStockStrategy === 'sold_out';
    const showNotify =
      !isGiftCard &&
      outOfStockStrategy === 'notifications' &&
      !anyVariantAvailable;

    // Don't show button for GWP or bookeasy products
    if (isGWP || isBookEasy) {
      return '';
    }

    // Buttons that don't need the addToCart component wrapper
    // These are simple buttons that just link to product page or show state
    if (showNotify || isSoldOut || hasMultipleVariants) {
      if (showNotify) {
        return `
          <div
            class="flex w-full max-w-full flex-col items-start justify-end text-sm"
            data-component="addToCart"
            data-loading-text="${labelLoading}"
            data-product-title="${productTitle}"
            data-product-price="${productPrice}"
            data-shop-currency="${shopCurrency}"
            data-variant-id="${variantId}"
            data-is-mto-product="false"
            data-is-preorder-product="false"
          >
            <button
              class="${baseClass}"
              data-button-notify-me
              data-variant-id="${variantId}"
            >
              ${textNotifyMe}
            </button>
          </div>
        `;
      }

      if (hasMultipleVariants) {
        const selectText = isGiftCard ? textChooseAmount : textSelectSize;
        return `
          <a
            href="${productUrl}"
            class="${baseClass}"
            data-button-select-size
          >
            ${selectText}
          </a>
        `;
      }
    }

    // Add to Cart / MTO / Preorder button - NEEDS wrapper with data-component="addToCart"
    if (isAvailable || isMTO || isPreorder) {
      let buttonTextValue = `${textPrefix} ${textAddToCart}`;
      if (isPreorder) {
        buttonTextValue = `${textPrefix} ${textPreorder}`;
      } else if (isMTO) {
        buttonTextValue = `${textPrefix} ${textMto}`;
      }

      return `
        <div
          class="flex w-full max-w-full flex-col items-start justify-end text-sm"
          data-component="addToCart"
          data-loading-text="${labelLoading}"
          data-product-title="${productTitle}"
          data-product-price="${productPrice}"
          data-shop-currency="${shopCurrency}"
          data-variant-id="${variantId}"
          data-is-mto-product="${isMTO}"
          data-is-preorder-product="${isPreorder}"
        >
          <button
            class="${baseClass}"
            data-button-add-to-cart
          >
            ${buttonTextValue}
          </button>
        </div>
      `;
    }

    // Fallback - link to product
    return `
      <a
        href="${productUrl}"
        class="${baseClass}"
      >
        ${textGoToProduct}
      </a>
    `;
  };

  // Helper: Build quote card for grid gap filling
  const buildQuoteCard = (quoteBlocks) => {
    if (!quoteBlocks || quoteBlocks.length === 0) {
      return '';
    }

    // Random selection using date-based modulo (similar to Liquid template)
    const now = new Date();
    const nanoSeconds = now.getTime() * 1000000 + now.getMilliseconds() * 1000;
    const randomIndex = nanoSeconds % quoteBlocks.length;
    const quote = quoteBlocks[randomIndex];

    // Validate quote object
    if (!quote || !quote.quote) {
      return '';
    }

    return `
      <div class="product quote aspect-[0.66] w-full">
        <div class="flex aspect-[0.65] h-full flex-col items-center justify-center bg-canvas text-[28px] text-xl mobile:text-xl tab_p:aspect-[0.75] tab_p:h-auto tab_p:text-2xl tab_l:text-lg">
          <blockquote
            style="color: ${quote.quote_color || '#000000'}"
            class="m-0 p-2 text-center font-secondary text-[28px] font-medium leading-[1.25] text-black"
          >
            ${quote.quote}
          </blockquote>
        </div>
      </div>
    `;
  };

  // Enrich wishlist items with fresh Shopify product data
  const enrichWithShopifyData = async (wishlistItems) => {
    const enrichPromises = wishlistItems.map(async (item) => {
      try {
        // Validate item has required properties
        if (!item || !item.du) {
          return item;
        }

        // Get product handle from cprops or extract from URL
        const handle =
          item.cprops?.handle || item.du.split('/products/')[1]?.split('?')[0];

        if (!handle) {
          return item;
        }

        // Fetch fresh product data from Shopify
        const response = await fetch(`/products/${handle}.js`);

        if (!response.ok) {
          return item;
        }

        const productData = await response.json();

        // Find the matching variant
        const variant = productData.variants.find((v) => v.id === item.epi);

        if (!variant) {
          return item;
        }

        // Merge fresh Shopify data with existing item (keeping cprops metafields)
        return {
          ...item,
          // Update basic Swym properties with fresh data
          pr: variant.price,
          iu:
            variant.featured_image?.src ||
            productData.featured_image ||
            item.iu,
          dt: productData.title,
          du: `${window.location.origin}${productData.url}`,

          // Add fresh Shopify data
          shopifyData: {
            type: productData.type,
            tags: productData.tags || [],
            images: productData.images || [],
            price_varies: productData.price_varies,
            available: variant.available,
            inventory_quantity: variant.inventory_quantity || 0,
            compare_at_price: variant.compare_at_price || 0,
            variants: productData.variants,
          },

          // Keep cprops metafields (they don't change often)
          cprops: item.cprops || {},
        };
      } catch (error) {
        return item; // Return original item if fetch fails
      }
    });

    const enrichedItems = await Promise.all(enrichPromises);
    return enrichedItems;
  };

  // Build product card HTML using hybrid data (Shopify + cprops) - matching product-thumb.liquid
  const buildProductCard = (item) => {
    // Validate required item properties
    if (!item || !item.du) {
      return '';
    }

    console.log('😚', item);

    const productPath = getProductPath(item.du) || '';
    const imageUrl = item.iu || ''; // Fresh from Shopify
    const title = item.dt || ''; // Fresh from Shopify
    const price = item.pr || 0; // Fresh from Shopify
    const productId = item.empi || '';
    const variantId = item.epi || '';
    const shopifyData = item.shopifyData || {}; // Fresh Shopify data
    const cprops = item.cprops || {}; // Metafields

    // Get product handle from URL
    const handle =
      item.du && typeof item.du === 'string'
        ? item.du.split('/products/')[1]?.split('?')[0] || ''
        : '';

    // Determine layout classes from cprops metafield
    const isDoubleWidth = cprops.is_double_width;
    const doubleWidthClasses = isDoubleWidth
      ? 'product-thumb--double-width !w-full col-span-2'
      : '';
    const aspectRatio = isDoubleWidth
      ? ''
      : 'tab_p:aspect-[1/1.33] aspect-[1/1.53]';

    // Build images - use fresh images from shopifyData
    const images = shopifyData.images || cprops.images || [];
    const hasMultipleImages = images.length > 1;

    // First image with responsive classes
    const firstImageHtml = `
      <img
        src="${imageUrl}"
        alt="${title}"
        class="absolute left-0 top-0 h-full w-full object-cover md:hidden"
        loading="lazy"
      />
      <img
        src="${imageUrl}"
        alt="${title}"
        class="absolute left-0 top-0 hidden h-full w-full object-cover md:block"
        loading="lazy"
      />
    `;

    // Second image for hover effect (desktop only)
    let secondImageHtml = '';
    if (hasMultipleImages) {
      // Find non-variant image for hover if available
      let secondImage = images[1] || '';
      secondImageHtml = `
        <img
          src="${secondImage}"
          alt="${title}"
          class="!absolute top-0 object-cover left-0 h-full w-full opacity-0 group-hover:opacity-100 transition-all duration-300 hidden md:block ease-linear bg-canvas"
          loading="lazy"
        />
      `;
    }

    // Determine "From" price text
    const showFrom = shopifyData.price_varies || cprops.price_varies;

    return `
      <div
        class="product product-thumb h-[100%] relative ${doubleWidthClasses}"
        data-component="productThumb"
      >
        <div class="flex flex-col gap-4 h-[100%]">
          <div class="product-thumb__top group relative ${aspectRatio}">
            <a
              href="${productPath}"
              class="product__top block group h-full w-full text-black no-underline ${hasMultipleImages ? 'has-hover' : ''}"
              data-product-click="true"
              data-product-title="${title}"
              data-product-price="${price}"
              data-product-id="${productId}"
            >
              ${firstImageHtml}
              ${secondImageHtml}
              ${buildProductSplashes(item, shopifyData, cprops)}
            </a>

            ${buildWishlistButton(productId, variantId, item.du, title, price, imageUrl)}

            ${buildColorSelector(cprops, handle)}
          </div>

          <div class="flex flex-grow flex-col">
            <a
              href="${productPath}"
              class="h-max text-black no-underline !border-none ${hasMultipleImages ? 'has-hover' : ''}"
              data-product-click="true"
              data-product-title="${title}"
              data-product-price="${price}"
              data-product-id="${productId}"
            >
              <div class="product__information">
                <div class="text-left font-secondary text-sm font-medium text-black">
                  ${title}
                </div>
                <div class="product__price-container">
                  <div class="product__price current">
                    ${showFrom ? `${textFrom} ` : ''}${formatMoney(price)}
                  </div>
                  ${
                    (shopifyData.compare_at_price || cprops.compare_at_price) &&
                    (shopifyData.compare_at_price || cprops.compare_at_price) >
                      price
                      ? `
                    <div class="product__price compare">${formatMoney(shopifyData.compare_at_price || cprops.compare_at_price)}</div>
                  `
                      : ''
                  }
                </div>
              </div>
            </a>
          </div>
          <div class="product-thumb__buttons">
            ${buildAddToCartButton(item, shopifyData, cprops)}
          </div>
        </div>
      </div>
    `;
  };

  // Helper: Build wishlist button - matching swym-wishlist-button.liquid structure
  const buildWishlistButton = (
    productId,
    variantId,
    productUrl,
    title,
    price,
    imageUrl
  ) => {
    // Validate price
    const validPrice = price && !isNaN(price) ? price : 0;

    return `
      <div class="swym-wishlist-button-wrapper absolute bottom-1 right-1 md:top-2 md:right-2 z-10" data-component="swymWishlistButton">
        <button
          type="button"
          data-wishlist-button
          data-product-id="${productId}"
          data-variant-id="${variantId}"
          data-product-url="${productUrl}"
          data-product-title="${title}"
          data-variant-price="${validPrice * 100}"
          data-product-image="${imageUrl}"
          data-label-add="${labelAdd}"
          data-label-remove="${labelRemove}"
          class="flex items-center justify-center w-10 h-10 transition-all duration-200 hover:scale-110"
          aria-label="${labelRemove}"
        >
          <div data-unfilled-icon class="hidden w-6 h-6 transition-opacity">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.226 6.87717L11.961 7.79592C11.981 7.82094 12.019 7.82094 12.039 7.79592L12.774 6.87717C13.4699 6.00733 14.5235 5.50098 15.6374 5.50098C16.6099 5.50098 17.5426 5.88731 18.2302 6.57498L18.3281 6.67285C19.0785 7.42319 19.5 8.44087 19.5 9.50201V9.86553C19.5 10.3899 19.4155 10.9108 19.2496 11.4083L19.0534 11.9971C18.7287 12.9712 18.2342 13.8801 17.5928 14.6818L17.4072 14.9138C16.9289 15.5118 16.3858 16.0549 15.7878 16.5332L15.6832 16.6169C14.7951 17.3274 13.8071 17.9031 12.7511 18.3255C12.2689 18.5184 11.7311 18.5184 11.2489 18.3255C10.1929 17.9031 9.20492 17.3274 8.31681 16.6169L8.21216 16.5332C7.61423 16.0549 7.07112 15.5118 6.59277 14.9138L6.40719 14.6818C5.76579 13.8801 5.27132 12.9712 4.94663 11.9971L4.75036 11.4083C4.58454 10.9108 4.5 10.3899 4.5 9.86553V9.50201C4.5 8.44087 4.92154 7.42319 5.67187 6.67285L5.76975 6.57498C6.45742 5.88731 7.3901 5.50098 8.36261 5.50098C9.47655 5.50098 10.5301 6.00733 11.226 6.87717Z" stroke="currentColor" stroke-width="1.25"/>
            </svg>
          </div>
          <div data-filled-icon class="w-6 h-6 transition-opacity text-red-500">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.226 6.87717L11.961 7.79592C11.981 7.82094 12.019 7.82094 12.039 7.79592L12.774 6.87717C13.4699 6.00733 14.5235 5.50098 15.6374 5.50098C16.6099 5.50098 17.5426 5.88731 18.2302 6.57498L18.3281 6.67285C19.0785 7.42319 19.5 8.44087 19.5 9.50201V9.86553C19.5 10.3899 19.4155 10.9108 19.2496 11.4083L19.0534 11.9971C18.7287 12.9712 18.2342 13.8801 17.5928 14.6818L17.4072 14.9138C16.9289 15.5118 16.3858 16.0549 15.7878 16.5332L15.6832 16.6169C14.7951 17.3274 13.8071 17.9031 12.7511 18.3255C12.2689 18.5184 11.7311 18.5184 11.2489 18.3255C10.1929 17.9031 9.20492 17.3274 8.31681 16.6169L8.21216 16.5332C7.61423 16.0549 7.07112 15.5118 6.59277 14.9138L6.40719 14.6818C5.76579 13.8801 5.27132 12.9712 4.94663 11.9971L4.75036 11.4083C4.58454 10.9108 4.5 10.3899 4.5 9.86553V9.50201C4.5 8.44087 4.92154 7.42319 5.67187 6.67285L5.76975 6.57498C6.45742 5.88731 7.3901 5.50098 8.36261 5.50098C9.47655 5.50098 10.5301 6.00733 11.226 6.87717Z" stroke="currentColor" stroke-width="1.25"/>
            </svg>
          </div>
        </button>
      </div>
    `;
  };

  // Set heights for double-width products to match single-width product heights
  const setDoubleWidthHeights = () => {
    // Find the grid container
    const gridContainer = itemsContainer;
    if (!gridContainer) return;

    // Find all double-width products
    const doubleWidthProducts = gridContainer.querySelectorAll(
      '.product-thumb--double-width'
    );

    if (doubleWidthProducts.length === 0) return;

    // Find a single-width product's image element to measure
    const singleWidthProduct = gridContainer.querySelector(
      '.product-thumb:not(.product-thumb--double-width) .product__top'
    );

    if (singleWidthProduct) {
      const singleWidth = singleWidthProduct.clientWidth;
      const singleWidthHeight = singleWidth * 1.33;

      if (singleWidthHeight > 0) {
        // Apply height to all double-width products
        doubleWidthProducts.forEach((product) => {
          product.style.setProperty(
            '--double-width-height',
            `${singleWidthHeight}px`
          );
        });
      }
    }
  };

  // Load and display wishlist
  const loadWishlist = async (swat) => {
    // Show loader
    loader.classList.remove('hidden');
    emptyState.classList.add('hidden');
    errorState.classList.add('hidden');
    itemsContainer.classList.add('hidden');

    try {
      // Clear cache to force fresh fetch
      window.swymWishLists = null;

      // Fetch lists
      const lists = await new Promise((resolve, reject) => {
        swat.fetchLists({
          callbackFn: (response) => {
            resolve(response);
          },
          errorFn: (error) => {
            reject(error);
          },
        });
      });

      // Get first list (single list mode)
      const wishlist = lists && lists[0];

      if (!wishlist) {
        loader.classList.add('hidden');
        emptyState.classList.remove('hidden');
        return;
      }

      if (!wishlist.listcontents || wishlist.listcontents.length === 0) {
        loader.classList.add('hidden');
        emptyState.classList.remove('hidden');
        return;
      }

      // Enrich with fresh Shopify data
      const enrichedItems = await enrichWithShopifyData(wishlist.listcontents);

      // Get quote blocks from data attribute
      const quotesData = itemsContainer.dataset.quotes;
      let quoteBlocks = [];
      if (quotesData) {
        try {
          const parsedData = JSON.parse(quotesData);
          quoteBlocks = parsedData.quotes || [];
        } catch (error) {
          // Error parsing quote blocks - fallback to empty array
        }
      }

      // Render products with row-tracking logic (similar to collection-products.liquid)
      const htmlItems = [];
      let rowCount = 1; // Track position in 4-column grid

      enrichedItems.forEach((item) => {
        const isDoubleWidth = item.cprops?.is_double_width;

        // If double-width product won't fit in current row (row_count == 4), add quote first
        if (isDoubleWidth && rowCount === 4) {
          htmlItems.push(buildQuoteCard(quoteBlocks));
          rowCount = 1;
        }

        // Render the product
        htmlItems.push(renderProduct(item));

        // Update row count
        if (isDoubleWidth) {
          rowCount += 2;
        } else {
          rowCount += 1;
        }

        // Reset row count if we exceed 4 columns
        if (rowCount > 4) {
          rowCount = 1;
        }
      });

      if (htmlItems.length === 0) {
        loader.classList.add('hidden');
        emptyState.classList.remove('hidden');
        return;
      }

      itemsContainer.innerHTML = htmlItems.join('');

      // Hide loader, show items
      loader.classList.add('hidden');
      itemsContainer.classList.remove('hidden');

      // Re-initialize components for newly added product cards
      const wishlistButtons = itemsContainer.querySelectorAll(
        '[data-component="swymWishlistButton"]'
      );
      const addToCartComponents = itemsContainer.querySelectorAll(
        '[data-component="addToCart"]'
      );
      const productThumbs = itemsContainer.querySelectorAll(
        '[data-component="productThumb"]'
      );

      // Initialize wishlist buttons
      if (wishlistButtons.length > 0) {
        import('~/js/components/swym-wishlist-button').then((module) => {
          const swymWishlistButton = module.default;
          wishlistButtons.forEach((button) => {
            swymWishlistButton(button);
          });
        });
      }

      // Initialize add to cart components
      if (addToCartComponents.length > 0) {
        import('~/js/components/add-to-cart').then((module) => {
          const addToCart = module.default;
          addToCartComponents.forEach((component) => {
            addToCart(component);
          });
        });
      }

      // Initialize product thumbs
      if (productThumbs.length > 0) {
        import('~/js/components/product-thumb').then((module) => {
          const productThumb = module.default;
          productThumbs.forEach((thumb) => {
            productThumb(thumb);
          });
        });
      }

      // Set double-width heights after DOM is ready
      setTimeout(() => {
        setDoubleWidthHeights();
      }, 100);

      // Disconnect old ResizeObserver if it exists to prevent memory leak
      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      // Create new ResizeObserver to recalculate on window resize
      resizeObserver = new ResizeObserver(() => {
        setDoubleWidthHeights();
      });
      resizeObserver.observe(itemsContainer);
    } catch (error) {
      // Error loading wishlist - show error state
      loader.classList.add('hidden');
      errorState.classList.remove('hidden');
    }
  };

  // Initialize
  const init = () => {
    // Wait for Swym to load
    const initSwym = (swat) => {
      loadWishlist(swat);

      // Listen for wishlist changes
      swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, () => {
        setTimeout(() => loadWishlist(swat), 500);
      });

      swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, () => {
        setTimeout(() => loadWishlist(swat), 500);
      });
    };

    // Check if Swym is already loaded
    if (window._swat) {
      initSwym(window._swat);
    } else {
      if (!window.SwymCallbacks) {
        window.SwymCallbacks = [];
      }
      window.SwymCallbacks.push(initSwym);
    }

    // Retry button
    if (retryButton) {
      retryButton.addEventListener('click', () => {
        if (window._swat) {
          loadWishlist(window._swat);
        }
      });
    }
  };

  init();
};
